import * as React from 'react'

import { cn } from '@/lib/utils'

function Table({
  className,
  striped = true,
  alignTop = false,
  darkMode = false,
  bordered = false,
  ...props
}: React.ComponentProps<'table'> & {
  striped?: boolean
  alignTop?: boolean
  darkMode?: boolean
  bordered?: boolean
}) {
  const classNames = [
    'w-full',
    'caption-bottom',
    'relative',
    'text-sm',
    'text-body-base',
    'h-fit',
    '[&_thead_th]:uppercase',
  ]

  if (darkMode) {
    classNames.push(
      '[&_tr_th]:bg-black',
      '[&_tr_td]:bg-black',
      '[&_tr_th]:text-white',
      '[&_tr_td]:text-white',
      '[&_tr]:border-gray-800',
      '[&_tfoot]:border-gray-800',
    )
  } else {
    classNames.push(
      '[&_tr_th]:bg-[#F8FAFC] [&_tr_th]:text-muted-foreground',
      '[&_tr:nth-child(odd)_td]:bg-white',
    )

    if (striped) {
      classNames.push('[&_tr:nth-child(even)_td]:bg-gray-50')
    }
  }

  if (bordered) {
    classNames.push('border', '[&_th]:border', '[&_td]:border')
  }

  if (alignTop) {
    classNames.push('[&_td]:align-top')
  }

  return (
    <table data-slot="table" className={cn(classNames, className)} {...props} />
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('[&_tr]:border-b', className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
        className,
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        `hover:bg-muted/50 data-[state=selected]:bg-muted border-b
        transition-colors`,
        className,
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        `text-foreground h-10 px-4 py-2.5 text-left align-middle text-[11px]
        font-medium [&:has([role=checkbox])]:pr-0
        *:[[role=checkbox]]:translate-y-0.5`,
        className,
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        `px-4 py-3 align-middle [&:has([role=checkbox])]:pr-0
        [&>[role=checkbox]]:translate-y-[2px]`,
        className,
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('text-muted-foreground mt-4 text-sm', className)}
      {...props}
    />
  )
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
